import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Ensure default Leaflet marker assets load correctly under bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

/**
 * UserLocation Component
 * Displays a user's location on an embedded map
 */
const UserLocation = ({ config, strings }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [location, setLocation] = useState(null);

    // Create custom avatar marker icon - must be before conditional returns (hooks rule)
    const customIcon = useMemo(() => {
        if (!location) {
            return new L.Icon.Default();
        }

        const avatarUrl = config.avatarUrl;
        const displayName = config.displayName || strings.userLocation || 'User Location';
        const safeAlt = displayName.replace(/"/g, '&quot;');

        if (!avatarUrl) {
            return L.icon({
                iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
                iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41],
            });
        }

        const iconHtml = `
            <div class="member-marker" data-user-id="${config.userId || ''}">
                <img src="${avatarUrl}" alt="${safeAlt}" class="marker-avatar" 
                     onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNjY2MiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iI2ZmZiIvPgo8cGF0aCBkPSJNMzAgMzJjMC01LjUyMy00LjQ3Ny0xMC0xMC0xMHMtMTAgNC40NzctMTAgMTAiIGZpbGw9IiNmZmYiLz4KPC9zdmc+'" />
            </div>
        `;

        return L.divIcon({
            html: iconHtml,
            className: 'custom-member-marker',
            iconSize: [40, 40],
            iconAnchor: [20, 40],
            popupAnchor: [0, -40],
        });
    }, [location, config.avatarUrl, config.userId, config.displayName, strings.userLocation]);

    useEffect(() => {
        // If location is provided in config, use it
        if (config.location && config.location.latitude && config.location.longitude) {
            setLocation(config.location);
            setLoading(false);
        } else {
            // Otherwise fetch the location
            fetchUserLocation();
        }
    }, [config]);

    const fetchUserLocation = async () => {
        try {
            const formData = new FormData();
            formData.append('action', 'gw_bp_get_location');
            formData.append('user_id', config.userId);
            formData.append('nonce', window.gwBpUserLocationAjax?.nonce || '');

            const response = await fetch(window.gwBpUserLocationAjax?.ajaxurl || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (data.success && data.data) {
                setLocation(data.data);
            } else {
                setError(strings.noLocation || 'No location set');
            }
        } catch (err) {
            console.error('Error fetching user location:', err);
            setError(strings.error || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="gw-bp-user-location-loading">
                <div className="loading-spinner"></div>
                <p>{strings.loading || 'Loading...'}</p>
            </div>
        );
    }

    if (error || !location || !location.latitude || !location.longitude) {
        return (
            <div className="gw-bp-user-location-empty">
                <div className="empty-state">
                    <svg className="empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <p>{error || (strings.noLocation || 'No location set')}</p>
                </div>
            </div>
        );
    }

    const position = [parseFloat(location.latitude), parseFloat(location.longitude)];
    const mapHeight = config.height || '400px';
    const zoom = config.zoom || 12;

    return (
        <div className="gw-bp-user-location-container">
            {config.showTitle !== false && (
                <div className="user-location-header">
                    <h4>{config.title || (strings.userLocation || 'User Location')}</h4>
                </div>
            )}
            
            {location.address && config.showAddress !== false && (
                <div className="user-location-address">
                    <svg className="address-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <span>{location.address}</span>
                </div>
            )}

            <div className="user-location-map" style={{ height: mapHeight }}>
                <MapContainer
                    center={position}
                    zoom={zoom}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={config.scrollWheelZoom !== false}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={position} icon={customIcon}>

                    </Marker>
                </MapContainer>
            </div>
        </div>
    );
};

export default UserLocation;
